import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalEmployers: 0,
    totalCandidates: 0,
    totalConsultants: 0,
    totalJobs: 0,
    activeJobs: 0,
    pendingJobs: 0,
    totalApplications: 0,
    pendingApplications: 0,
    totalCompanies: 0,
    verifiedCompanies: 0,
    recentUsers: 0,
    recentJobs: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      // console.log('Admin stats response:', response);

      if (response && response.data) {
        setStats(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Stats error:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch statistics');
      toast.error('Failed to fetch statistics');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="adm-dashboard">
        <div className="adm-loading">
          <div className="adm-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="adm-dashboard">
        <div className="adm-error">
          <span className="material-symbols-outlined" style={{fontSize:48,color:'#ef4444'}}>error</span>
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button className="adm-btn adm-btn-primary" onClick={fetchStats}>Retry</button>
        </div>
      </div>
    );
  }

  const navItems = [
    { to: '/admin/users', icon: 'group', label: 'Users', color: '#0f5492', bg: '#cae5fe', subtitle:'Manage users'},
    { to: '/admin/jobs', icon: 'work', label: 'Jobs', color: '#8f4de4', bg: '#eadffc', subtitle:'Manage jobs'},
    { to: '/admin/companies', icon: 'business', label: 'Companies', color: '#10a966', bg: '#ccf6e9', subtitle:'Manage Companies' },
    { to: '/admin/applications', icon: 'description', label: 'Applications', color: '#fd5b16', bg: '#fde2d7', subtitle:'Manage Applications'},
    { to: '/admin/master-data', icon: 'database', label: 'Master Data', color: '#f59e0b', bg: '#fff4c6', subtitle:'Master Data Management' },
    { to: '/admin/notifications', icon: 'notifications', label: 'Notifications', color: '#0f5492', bg: '#cae5fe', subtitle:'Manage Notifications'},
  ];

  return (
    <div className="adm-dashboard">
      {/* Header */}
      <div className="adm-header">
        <div className="adm-header-left">
          <div className="adm-header-icon">
            <span className="material-symbols-outlined">admin_panel_settings</span>
          </div>
          <div>
            <h1>Admin Dashboard</h1>
            <p>Welcome back, <strong>{user?.name}</strong> — here's what's happening today.</p>
          </div>
        </div>
        <button onClick={handleLogout} className="adm-logout-btn">
          <span className="material-symbols-outlined">logout</span>
          Logout
        </button>
      </div>

      {/* Stats Grid */}
      <div className="adm-stats-grid">
        <div className="adm-stat-card adm-stat-blue">
          <div className='adm-card-top'>
           <div className="adm-stat-icon"><span className="material-symbols-outlined">group</span></div>
           <div className="adm-stat-number">{stats.totalUsers}</div>
          </div>
          <div className="adm-stat-label">Total Users</div>
          <div className="adm-stat-sub">
            <span>{stats.totalEmployers} employers</span>
            <span>{stats.totalCandidates} candidates</span>
          </div>
        </div>

        <div className="adm-stat-card adm-stat-orange">
          <div className='adm-card-top'>
           <div className="adm-stat-icon"><span className="material-symbols-outlined">work</span></div>
           <div className="adm-stat-number">{stats.totalJobs}</div>
          </div>
          <div className="adm-stat-label">Total Jobs</div>
          <div className="adm-stat-sub">
            <span className="adm-badge-green">{stats.activeJobs} active</span>
            <span className="adm-badge-yellow">{stats.pendingJobs} pending</span>
          </div>
        </div>

        <div className="adm-stat-card adm-stat-purple">
          <div className='adm-card-top'>
          <div className="adm-stat-icon"><span className="material-symbols-outlined">description</span></div>
          <div className="adm-stat-number">{stats.totalApplications}</div>
          </div>
          <div className="adm-stat-label">Applications</div>
          <div className="adm-stat-sub">
            <span className="adm-badge-yellow">{stats.pendingApplications} pending</span>
          </div>
        </div>

        <div className="adm-stat-card adm-stat-green">
          <div className='adm-card-top'>
          <div className="adm-stat-icon"><span className="material-symbols-outlined">business</span></div>
          <div className="adm-stat-number">{stats.totalCompanies}</div>
          </div>
          <div className="adm-stat-label">Companies</div>
          <div className="adm-stat-sub">
            <span className="adm-badge-green">{stats.verifiedCompanies} verified</span>
          </div>
        </div>

        <div className="adm-stat-card adm-stat-pink">
          <div className='adm-card-top'>
          <div className="adm-stat-icon"><span className="material-symbols-outlined">support_agent</span></div>
          <div className="adm-stat-number">{stats.totalConsultants}</div>
          </div>
          <div className="adm-stat-label">Consultants</div>
          <div className="adm-stat-sub">
            <span>{stats.recentUsers} new this week</span>
          </div>
        </div>

        <div className="adm-stat-card adm-stat-amber">
          <div className='adm-card-top'>
          <div className="adm-stat-icon"><span className="material-symbols-outlined">trending_up</span></div>
          <div className="adm-stat-number">{stats.recentJobs}</div>
          </div>
          <div className="adm-stat-label">New Jobs (This Week)</div>
          <div className="adm-stat-sub">
            <span>{stats.recentUsers} new users</span>
          </div>
        </div>

      </div>

      {/* Quick Navigation */}
      <div className="adm-section">
        <h2 className="adm-section-title">
          {/* <span className="material-symbols-outlined">grid_view</span> */}
          Quick Navigation
        </h2>
        <div className="adm-nav-grid">
          {navItems.map(item => (
            <Link key={item.to} to={item.to} className="adm-nav-card" style={{'--nav-color': item.color, '--nav-bg': item.bg}}>
              <div className="adm-nav-icon">
                <span className="material-symbols-outlined">{item.icon}</span>
              </div>
              <div>
              <h3 className="adm-nav-label">{item.label}</h3>
              <p className='adm-subtitle'>{item?.subtitle}</p>
              </div>
              <span className="material-symbols-outlined adm-nav-arrow">arrow_forward</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
